import Button from 'components/button';
import EditorCompV2 from 'components/editorCompV2';
import FileUpload from 'components/fileUpload';
import { ImagePreview } from 'components/fileUpload/ImagePreview';
import ImageResponsive from 'components/imageResponsive';
import Input from 'components/input';
import Textarea from 'components/textarea';
import { useFormik } from 'formik';
import get from 'lodash/get';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { imageLink } from 'utils/imageLink';
import * as yup from 'yup';

import { ENewsStatus } from '@app/common/types';

export interface INewsFormValues {
  newsTitleVi: string;
  newsTitleEn: string;
  newsIntroductionVi: string;
  newsIntroductionEn: string;
  newsContentVi: string;
  newsContentEn: string;
  newsCoverImg: File | null;
  status: ENewsStatus;
  newsCoverImgId?: string;
}

export interface INewsFormProps {
  initialValues?: INewsFormValues;
  loading?: boolean;
  onSubmit: (_values: INewsFormValues) => void;
}

const NewsForm: React.FC<INewsFormProps> = ({ initialValues, onSubmit, loading = false }) => {
  const { t } = useTranslation();

  const formik = useFormik<INewsFormValues>({
    initialValues: initialValues ?? {
      newsTitleVi: '',
      newsTitleEn: '',
      newsIntroductionVi: '',
      newsIntroductionEn: '',
      newsContentVi: '',
      newsContentEn: '',
      newsCoverImg: null,
      status: ENewsStatus.active
    },
    enableReinitialize: true,
    validationSchema: yup.object().shape({
      newsTitleVi: yup.string().trim().required('admin.label.news_form.news_title_required_message'),
      newsTitleEn: yup.string().trim(),
      newsIntroductionVi: yup.string().trim().required('admin.label.news_form.news_intro_required_message'),
      newsIntroductionEn: yup.string().trim(),
      newsContentVi: yup.string().trim().required('admin.label.news_form.news_content_required_message'),
      newsContentEn: yup.string().trim(),
      ...(!initialValues ? { newsCoverImg: yup.mixed().required('admin.label.news_form.news_content_required_message') } : {})
    }),
    onSubmit: (values) => onSubmit(values)
  });

  const handleUploadImage = useCallback((event: any) => {
    const files = event.currentTarget.files;

    if (get(files, 'length', 0)) formik.setFieldValue('newsCoverImg', files[0]);
  }, []);

  return (
    <form onSubmit={formik.handleSubmit} className='form-control'>
      <div className='form-control__wrap'>
        <div className='form-control__item'>
          <label htmlFor='newsTitleVi' className='form-control__item-label required'>
            {t('admin.label.news_form.news_title_vi')}
          </label>
          <Input
            id='newsTitleVi'
            name='newsTitleVi'
            type='text'
            placeholder={t('admin.label.news_form.news_title_vi')}
            className='w-full'
            value={formik.values.newsTitleVi}
            onChange={formik.handleChange}
            errorMessage={formik.errors.newsTitleVi ? (t(formik.errors.newsTitleVi) as string) : ''}
          />
        </div>

        <div className='form-control__item'>
          <label htmlFor='newsTitleEn' className='form-control__item-label'>
            {t('admin.label.news_form.news_title_en')}
          </label>
          <Input
            id='newsTitleEn'
            name='newsTitleEn'
            type='text'
            placeholder={t('admin.label.news_form.news_title_en')}
            className='w-full'
            value={formik.values.newsTitleEn}
            onChange={formik.handleChange}
            errorMessage={formik.errors.newsTitleEn ? (t(formik.errors.newsTitleEn) as string) : ''}
          />
        </div>
      </div>

      <div className='form-control__wrap'>
        <div className='form-control__item'>
          <label htmlFor='newsIntroductionVi' className='form-control__item-label required'>
            {t('admin.label.news_form.news_introduction_vi')}
          </label>
          <Textarea
            id='newsIntroductionVi'
            name='newsIntroductionVi'
            placeholder={t('admin.label.news_form.news_introduction_vi')}
            className='w-full'
            rows={5}
            value={formik.values.newsIntroductionVi}
            onChange={formik.handleChange}
            errorMessage={formik.errors.newsIntroductionVi ? (t(formik.errors.newsIntroductionVi) as string) : ''}
          />
        </div>

        <div className='form-control__item'>
          <label htmlFor='newsIntroductionEn' className='form-control__item-label'>
            {t('admin.label.news_form.news_introduction_en')}
          </label>
          <Textarea
            id='newsIntroductionEn'
            name='newsIntroductionEn'
            placeholder={t('admin.label.news_form.news_introduction_en')}
            className='w-full'
            rows={5}
            value={formik.values.newsIntroductionEn}
            onChange={formik.handleChange}
            errorMessage={formik.errors.newsIntroductionEn ? (t(formik.errors.newsIntroductionEn) as string) : ''}
          />
        </div>
      </div>

      <div className='form-control__item'>
        <label htmlFor='newsContentVi' className='form-control__item-label required'>
          {t('admin.label.news_form.news_content_vi')}
        </label>
        <span className='form-control__item-helper-text'>{t('admin.text.using_bootstrap_helper_text')}</span>
        <EditorCompV2 value={formik.values.newsContentVi ?? ''} onChange={(content) => formik.setFieldValue('newsContentVi', content)} />
        {formik.errors.newsContentVi ? <span className='text-sm text-hl-primary'>{t(formik.errors.newsContentVi) as string}</span> : <></>}
      </div>

      <div className='form-control__item'>
        <label htmlFor='newsContentEn' className='form-control__item-label'>
          {t('admin.label.news_form.news_content_en')}
        </label>
        <span className='form-control__item-helper-text'>{t('admin.text.using_bootstrap_helper_text')}</span>
        <EditorCompV2 value={formik.values.newsContentEn ?? ''} onChange={(content) => formik.setFieldValue('newsContentEn', content)} />
        {formik.errors.newsContentEn ? <span className='text-sm text-hl-primary'>{t(formik.errors.newsContentEn) as string}</span> : <></>}
      </div>

      <div className='form-control__item'>
        <label htmlFor='newsCoverImg' className='form-control__item-label required'>
          {t('admin.label.news_form.news_cover_img')}
        </label>
        <FileUpload label={t('admin.label.update_image')} onChange={handleUploadImage} disabled={!!formik.values.newsCoverImg} className='w-fit' />
        <div className='w-1/3'>
          {formik.values.newsCoverImg ? (
            <ImagePreview imageFile={formik.values.newsCoverImg} onRemoveImage={() => formik.setFieldValue('newsCoverImg', null)} />
          ) : initialValues?.newsCoverImgId ? (
            <div className='relative w-full pb-100%'>
              <ImageResponsive alt='image-preview' src={imageLink(initialValues.newsCoverImgId)} />
            </div>
          ) : (
            <></>
          )}
        </div>
        {formik.errors.newsCoverImg ? <span className='validate-error'>{t(formik.errors.newsCoverImg as string) as string}</span> : <></>}
      </div>

      <div className='form-control__item flex items-center justify-center'>
        <Button label={t('admin.label.news.submit_button')} type='submit' isLoading={loading} />
      </div>
    </form>
  );
};

export default NewsForm;
